"use client";
import React, { useState } from "react";
import { Box, Container, Typography, Button, Chip } from "@mui/material";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const tabIcons = [
  IMAGES.ZAYN_ICON_1,
  IMAGES.ZAYN_ICON_2,
  IMAGES.ZAYN_ICON_3,
  IMAGES.ZAYN_ICON_4,
  IMAGES.ZAYN_ICON_5,
  IMAGES.ZAYN_ICON_6,
];
const tabImages = [
  IMAGES.ZAYN_IMG_1,
  IMAGES.ZAYN_IMG_2,
  IMAGES.ZAYN_IMG_3,
  IMAGES.ZAYN_IMG_4,
  IMAGES.ZAYN_IMG_5,
  IMAGES.ZAYN_IMG_6,
];
const tabImageAlts = [
  "AI recruiter anti-cheating monitor",
  "AI recruiter 50+ languages",
  "AI recruiting behavioral analysis",
  "AI interview summaries",
  "Team collaboration hub",
  "Custom AI interview creation",
];

const ZaynAIRecruitingSoftware = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [activeTab, setActiveTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleTabChange = (newTabId) => {
    if (newTabId === activeTab) return;
    setIsTransitioning(true);
    setActiveTab(newTabId);
    setTimeout(() => setIsTransitioning(false), 350);
  };

  const tabsRaw = t("zaynAIRecruitingSoftware.tabs", { returnObjects: true });
  const tabsData = Array.isArray(tabsRaw) ? tabsRaw : [];

  const tabs = tabsData.map((tab, i) => ({
    id: i,
    icon: tabIcons[i],
    image: tabImages[i],
    imagealt: tabImageAlts[i],
    ...tab,
  }));

  return (
    <Box
      sx={{
        py: { xs: 2, md: 6, lg: 3 },
        mt: { xs: "-4%", lg: 0 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            textAlign: isArabic ? "right" : "left",
            mb: { xs: 4, sm: 5, md: 6, lg: 8 },
            px: { xs: 2, sm: 3, md: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: isArabic ? "flex-start" : "flex-start",
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <Chip
              label={t("zaynAIRecruitingSoftware.chip")}
              sx={{
                backgroundColor: "transparent",
                border: "1px solid #DDE4E8",
                color: "#647482",
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                fontWeight: "400",
                textTransform: "capitalize",
                px: { xs: 0.5, sm: 0.8 },
                py: { xs: 1.2, sm: 1.5, md: 2.4 },
                fontFamily: "var(--font-plusjakarta)",
                borderRadius: "40px",
              }}
            />
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontSize: {
                xs: "26px",
                sm: "30px",
                md: "36px",
                lg: "38px",
                xl: "42px",
              },
              fontWeight: "bold",
              lineHeight: 1.2,
              color: "#000000",
              mb: 1,
              fontFamily: "var(--font-plusjakarta)",
              ml: isArabic ? 0 : { xs: 1, sm: 1.5, md: 0 },
              mr: isArabic ? { xs: 1, sm: 1.5, md: 0 } : 0,
            }}
          >
            {t("zaynAIRecruitingSoftware.heading1")}{" "}
            <span
              style={{
                color: "#775CE5",
                marginLeft: isArabic ? 0 : ".3%",
                marginRight: isArabic ? ".3%" : 0,
              }}
            >
              {t("zaynAIRecruitingSoftware.headingHighlight1")}
            </span>{" "}
            {t("zaynAIRecruitingSoftware.heading1End")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "26px", sm: "30px", md: "36px", lg: "42px" },
              fontWeight: "bold",
              lineHeight: 1.2,
              color: "#000000",
              mb: 2,
              fontFamily: "var(--font-plusjakarta)",
              ml: isArabic ? 0 : { xs: 1, sm: 1.5, md: 0 },
              mr: isArabic ? { xs: 1, sm: 1.5, md: 0 } : 0,
            }}
          >
            <span
              style={{
                color: "#775CE5",
                marginRight: isArabic ? 0 : ".3%",
                marginLeft: isArabic ? ".3%" : 0,
              }}
            >
              {t("zaynAIRecruitingSoftware.headingHighlight2")}
            </span>{" "}
            {t("zaynAIRecruitingSoftware.heading2End")}
          </Typography>
        </Box>
      </Container>

      {/* Top Divider */}
      <Box
        sx={{
          width: "100vw",
          height: "1px",
          bgcolor: "#E5E7EB",
          mb: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 2 },
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
        }}
      />

      <Container maxWidth="xl">
        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: 1.5, sm: 2, md: 3, lg: 2, xl: 6.7 },
            "@media (min-width:1400px) and (max-width:1535px)": { gap: 3.3 },
            "@media (min-width:1100px) and (max-width:1300px)": {
              justifyContent: "flex-start",
            },
            "@media (min-width: 900px) and (max-width: 1200px)": {
              gap: 2,
              px: 2,
              justifyContent: "flex-start",
            },
            mb: { xs: 4, sm: 5, md: 5.5, lg: 6 },
            overflowX: { xs: "hidden", md: "auto" },
            pb: 2,
            px: { xs: 1.5, sm: 2, md: 0 },
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#9048FB",
              borderRadius: "3px",
            },
            position: "relative",
          }}
        >
          {/* Left Arrow */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              left: 0,
              width: "40px",
              height: "100%",
              zIndex: 10,
              mt: "-2%",
            }}
          >
            <Button
              onClick={() =>
                handleTabChange(
                  activeTab === 0 ? tabs.length - 1 : activeTab - 1,
                )
              }
              sx={{
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <Box
                component="svg"
                sx={{ transform: "rotate(180deg)", width: 24, height: 24 }}
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 18l6-6-6-6"
                  stroke="#9048FB"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Button>
          </Box>

          {/* Right Arrow */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              right: 0,
              width: "40px",
              height: "100%",
              zIndex: 10,
              mt: "-2%",
            }}
          >
            <Button
              onClick={() =>
                handleTabChange(
                  activeTab === tabs.length - 1 ? 0 : activeTab + 1,
                )
              }
              sx={{
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <Box
                component="svg"
                sx={{ width: 24, height: 24 }}
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 18l6-6-6-6"
                  stroke="#9048FB"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Button>
          </Box>

          {tabs.map((tab) => (
            <Box
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              sx={{
                display: {
                  xs: activeTab === tab.id ? "flex" : "none",
                  md: "flex",
                },
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.3s ease-in-out",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {/* Active Tab - Pill Button */}
              <Button
                sx={{
                  bgcolor: "#9048FB",
                  color: "white",
                  px: { xs: 2.5, sm: 2.5, md: 3, lg: 3.5 },
                  py: { xs: 1.2, sm: 1.1, md: 1.2, lg: 1.4 },
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: { xs: "12px", sm: "13px", md: "14px" },
                  fontWeight: 500,
                  fontFamily: "var(--font-outfit)",
                  display:
                    activeTab === tab.id ? "flex" : { xs: "none", md: "none" },
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 1, md: 1.2 },
                  "&:hover": { bgcolor: "#9048FB" },
                  pointerEvents: { xs: "none", md: "auto" },
                  mt: { xs: "2%", md: "0" },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 20, sm: 20, md: 22, lg: 24 },
                    height: { xs: 20, sm: 20, md: 22, lg: 24 },
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src={tab.icon}
                    alt={tab.label}
                    width={24}
                    height={24}
                    style={{ objectFit: "contain" }}
                    unoptimized
                    placeholder="empty"
                    priority
                  />
                </Box>
                {tab.label}
              </Button>

              {/* Inactive Tab - Icon + Text */}
              <Box
                sx={{
                  display:
                    activeTab === tab.id
                      ? { xs: "none", md: "none" }
                      : { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: { xs: 0.8, sm: 0.9, md: 1 },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#9048FB",
                    color: "white",
                    borderRadius: "50%",
                    width: { xs: 34, sm: 36, md: 38 },
                    height: { xs: 34, sm: 36, md: 38 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: { xs: "6px", sm: "7px", md: "8px", lg: "9px" },
                  }}
                >
                  <Box
                    sx={{ width: "100%", height: "100%", position: "relative" }}
                  >
                    <Image
                      src={tab.icon}
                      alt={tab.label}
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "12px", sm: "13px", md: "14px" },
                    fontWeight: 500,
                    color: "#464f7b99",
                    fontFamily: "var(--font-outfit)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tab.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Bottom Divider */}
      <Box
        sx={{
          width: "100vw",
          height: "1px",
          bgcolor: "#E5E7EB",
          mb: { xs: 2, sm: 2.5, md: 3, lg: 3 },
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          mt: { xs: "-6%", sm: "-5%", md: "-2.2%", lg: "-2.5%", xl: "-2.2%" },
        }}
      />

      <Container maxWidth="xl">
        {/* Content Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: isArabic ? "60% 35%" : "35% 60%",
            },
            gap: { xs: 3, sm: 4, md: 5, lg: 6 },
            alignItems: "stretch",
            gridTemplateAreas: {
              xs: "'image' 'text'",
              md: isArabic ? "'text image'" : "'image text'",
            },
          }}
        >
          {/* Image Card */}
          <Box
            sx={{
              gridArea: "image",
              bgcolor: "white",
              borderRadius: 10,
              p: { xs: 2, sm: 2.5, md: 3 },
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "85%",
                height: "85%",
                minHeight: {
                  xs: "250px",
                  sm: "300px",
                  md: "350px",
                  lg: "400px",
                },
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Image
                key={tabs[activeTab].id}
                src={tabs[activeTab].image}
                alt={tabs[activeTab].imagealt}
                fill
                priority
                unoptimized
                placeholder="empty"
                style={{
                  objectFit: "contain",
                  padding: "10px",
                  transition: "opacity 0.4s ease-in-out",
                  opacity: isTransitioning ? 0 : 1,
                }}
              />
            </Box>
          </Box>

          {/* Text Content */}
          <Box
            sx={{
              gridArea: "text",
              bgcolor: "#F6F6F6",
              borderRadius: 10,
              p: { xs: 3, sm: 4, md: 5, lg: 6 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning
                ? isArabic
                  ? "translateX(20px)"
                  : "translateX(-20px)"
                : "translateX(0)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              textAlign: isArabic ? "right" : "left",
              ml: isArabic ? 0 : { xs: 1, sm: 2, md: 0 },
              mr: isArabic ? { xs: 1, sm: 2, md: 0 } : 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "24px", sm: "28px", md: "32px", lg: "36px" },
                color: "#020617",
                lineHeight: 1.3,
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 600,
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {tabs[activeTab].heading}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "17px" },
                color: "#6b7280",
                lineHeight: 1.7,
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
              }}
            >
              {tabs[activeTab].description}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ZaynAIRecruitingSoftware;
